import { ADMIN_RUNTIME_COMMANDS } from './adminRuntime';
import { notifyOperatorStateRefresh } from '../../features/home/livePublishEvents';
import { normalizeRuntimeSaveOutcome } from './saveOutcome';

/**
 * Compatibility port for the existing V2 editor.
 *
 * The editor keeps its narrow settings-client API while all environment
 * selection and transport work is delegated to AdminRuntime commands.
 */
export function createRuntimeSearchSettingsClient(runtime) {
  if (!runtime || typeof runtime.sendMessageWithResponse !== 'function') return null;

  const client = {
    loadSearchExperienceConfig: (shopId, mode = 'preview') => (
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD, { shopId, mode })
    ),

    loadSearchExperienceDefaults: (shopId, experience, mode = 'preview') => (
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD_DEFAULTS, {
        shopId,
        experience,
        mode,
      })
    ),

    saveSearchExperienceConfig: async (shopId, config, mode = 'preview') => {
      const result = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_SAVE_DRAFT,
        {
          shopId,
          config,
          mode,
        },
      );
      const outcome = normalizeRuntimeSaveOutcome(result, config);
      if (!outcome.success || outcome.errors.length > 0) return outcome;
      if (mode === 'preview'
        && runtime.supports(ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED)) {
        await runtime.sendMessageWithResponse(
          ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED,
          { shopId },
        );
      }
      notifyOperatorStateRefresh();
      return outcome;
    },

    describeSearchExperienceConfig: shopId => (
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DESCRIBE, { shopId })
    ),

    getSearchComponents: shopId => (
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COMPONENTS, { shopId })
    ),

    copySearchExperienceToProduction: shopId => (
      runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COPY_TO_PRODUCTION,
        { shopId },
      )
    ),
  };

  // The gateway validates on PUT but does not expose a non-mutating validation
  // endpoint. Do not advertise a command the selected runtime cannot execute.
  if (runtime.supports(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_VALIDATE)) {
    client.validateSearchExperienceConfig = (shopId, config) => (
      runtime.sendMessageWithResponse(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_VALIDATE, {
        shopId,
        config,
      })
    );
  }

  return Object.freeze(client);
}
